import React from "react";
import { FlagList, SectionGrid, SummaryGrid } from "../AdminAnalyticsCards";
import CssBarChart from "../charts/CssBarChart";
import AdminAnalyticsTable, { UserCell } from "../tables/AdminAnalyticsTable";
import { aa, formatDateTime, formatDay, formatDecimal, formatDuration, formatNumber, formatRate, getUserLabel, getUserSubLabel } from "../adminAnalyticsUtils";

const PLATFORM_LABELS = {
  deviceClass: {
    mobile: ["platform_mobile", "Mobile"],
    tablet: ["platform_tablet", "Tablet"],
    desktop: ["platform_desktop", "Desktop"],
    bot: ["platform_bot", "Bot"],
    unknown: ["unknown", "Unknown"],
  },
  osFamily: {
    ios: ["platform_ios", "iOS"],
    android: ["platform_android", "Android"],
    windows: ["platform_windows", "Windows"],
    macos: ["platform_macos", "macOS"],
    linux: ["platform_linux", "Linux"],
    chromeos: ["platform_chromeos", "ChromeOS"],
    other: ["platform_other", "Other"],
    unknown: ["unknown", "Unknown"],
  },
  browserFamily: {
    chrome: ["platform_chrome", "Chrome"],
    safari: ["platform_safari", "Safari"],
    firefox: ["platform_firefox", "Firefox"],
    edge: ["platform_edge", "Edge"],
    samsung: ["platform_samsung_internet", "Samsung Internet"],
    opera: ["platform_opera", "Opera"],
    other: ["platform_other", "Other"],
    unknown: ["unknown", "Unknown"],
  },
  appPlatform: {
    browser: ["platform_browser_mode", "Browser mode"],
    pwa: ["platform_reported_standalone_pwa", "Reported standalone / PWA mode"],
    unknown: ["unknown", "Unknown"],
  },
};

function getPlatformLabel(dimension, value) {
  const key = String(value || "unknown").trim().toLowerCase();
  const [translationKey, fallback] =
    PLATFORM_LABELS[dimension]?.[key] || PLATFORM_LABELS[dimension]?.unknown;
  return aa(translationKey, fallback);
}

function getPlatformSessions(row) {
  return row?.sessions ?? row?.count ?? 0;
}

function formatDistinctUsersSublabel(row) {
  return aa("distinct_users_sublabel", "{{count}} distinct users", {
    count: formatNumber(row?.distinctUsers),
  });
}

function formatCoveragePercent(value) {
  return `${formatDecimal(value, 2)}%`;
}

function formatCoverageDetail(populated, total) {
  return aa("platform_coverage_detail", "{{populated}} of {{total}} sessions", {
    populated: formatNumber(populated),
    total: formatNumber(total),
  });
}
function behaviorFlags(row) {
  return [{
    label: aa("feed", "Feed"),
    active: row?.openedFeed
  }, {
    label: aa("map", "Map"),
    active: row?.openedMap
  }, {
    label: aa("fishipedia", "Fishipedia"),
    active: row?.openedFishipedia
  }, {
    label: aa("create", "Create"),
    active: row?.openedCreatePost
  }, {
    label: aa("submitted", "Submitted"),
    active: row?.submittedPost
  }, {
    label: aa("gear", "Gear"),
    active: row?.openedGear
  }, {
    label: aa("trips", "Trips"),
    active: row?.openedTrips
  }, {
    label: aa("groups", "Groups"),
    active: row?.openedGroups
  }, {
    label: aa("messages", "Messages"),
    active: row?.openedMessages
  }];
}
function formatUsersSublabel(count) {
  return aa("users_sublabel", "{{count}} users", {
    count: formatNumber(count),
  });
}
function formatFeatureSublabel(feature) {
  return aa("feature_sublabel", "{{feature}} feature", {
    feature: feature || aa("unknown_2", "unknown"),
  });
}
export default function AdminAnalyticsEngagementTab({
  data
}) {
  const summary = data?.summary || {};
  const funnel = data?.funnel || {};
  const platformBreakdowns = data?.platformBreakdowns || {};
  const crossPlatformUsers = data?.crossPlatformUsers || {};
  const platformCompleteness = data?.platformDataCompleteness || {};
  const funnelRows = [{
    label: aa("feed_opened", "Feed opened"),
    value: funnel.feedOpened
  }, {
    label: aa("create_opened", "Create opened"),
    value: funnel.createPostOpened
  }, {
    label: aa("create_submitted", "Create submitted"),
    value: funnel.createPostSubmitted
  }];
  return <>
      <SummaryGrid cards={[{
      label: aa("active_users", "Active users"),
      value: formatNumber(summary.activeUsers)
    }, {
      label: aa("total_events", "Total events"),
      value: formatNumber(summary.totalEvents)
    }, {
      label: aa("total_sessions", "Total sessions"),
      value: formatNumber(summary.totalSessions)
    }, {
      label: aa("avg_duration", "Avg duration"),
      value: formatDuration(summary.avgSessionDurationSeconds)
    }, {
      label: aa("median_duration", "Median duration"),
      value: formatDuration(summary.medianSessionDurationSeconds)
    }, {
      label: aa("returning_users", "Returning users"),
      value: formatNumber(summary.returningUsers)
    }, {
      label: aa("feed_only_users", "Feed-only users"),
      value: formatNumber(summary.feedOnlyUsers)
    }, {
      label: aa("create_open_rate", "Create open rate"),
      value: formatRate(summary.createPostOpenRate)
    }, {
      label: aa("create_submit_rate", "Create submit rate"),
      value: formatRate(summary.createPostSubmitRate)
    }]} />

      <section
        className="admin-analytics-platforms"
        aria-labelledby="admin-analytics-platforms-title"
      >
        <div className="admin-analytics-section-heading">
          <h2 id="admin-analytics-platforms-title">
            {aa("platforms_and_devices", "Platforms and devices")}
          </h2>
          <p>
            {aa(
              "platforms_and_devices_description",
              "Bar values show sessions by operating system, device type, browser, and reported app mode; each sublabel shows distinct users."
            )}
          </p>
        </div>

        <SectionGrid>
          <CssBarChart
            title={aa("operating_systems", "Operating systems")}
            rows={platformBreakdowns.osFamily}
            getLabel={row => getPlatformLabel("osFamily", row?.osFamily)}
            getValue={getPlatformSessions}
            getSublabel={formatDistinctUsersSublabel}
          />
          <CssBarChart
            title={aa("device_types", "Device types")}
            rows={platformBreakdowns.deviceClass}
            getLabel={row => getPlatformLabel("deviceClass", row?.deviceClass)}
            getValue={getPlatformSessions}
            getSublabel={formatDistinctUsersSublabel}
          />
          <CssBarChart
            title={aa("browsers", "Browsers")}
            rows={platformBreakdowns.browserFamily}
            getLabel={row => getPlatformLabel("browserFamily", row?.browserFamily)}
            getValue={getPlatformSessions}
            getSublabel={formatDistinctUsersSublabel}
          />
          <CssBarChart
            title={aa("reported_app_mode", "Reported app mode")}
            rows={platformBreakdowns.appPlatform}
            getLabel={row => getPlatformLabel("appPlatform", row?.appPlatform)}
            getValue={getPlatformSessions}
            getSublabel={formatDistinctUsersSublabel}
          />
        </SectionGrid>

        <h3 className="admin-analytics-platforms__subheading">
          {aa("cross_platform_users", "Cross-platform users")}
        </h3>
        <SummaryGrid cards={[{
          label: aa("multiple_operating_systems", "Multiple operating systems"),
          value: formatNumber(crossPlatformUsers.multipleOperatingSystems)
        }, {
          label: aa("multiple_device_types", "Multiple device types"),
          value: formatNumber(crossPlatformUsers.multipleDeviceClasses)
        }, {
          label: aa("multiple_app_modes", "Multiple app modes"),
          value: formatNumber(crossPlatformUsers.multipleAppModes)
        }, {
          label: aa("any_cross_platform_usage", "Any cross-platform usage"),
          value: formatNumber(crossPlatformUsers.anyMultiplePlatformDimension)
        }]} />

        <h3 className="admin-analytics-platforms__subheading">
          {aa("platform_coverage", "Platform coverage")}
        </h3>
        <p className="admin-analytics-helper-note">
          {aa(
            "platform_coverage_description",
            "Coverage shows session rows with a known value. Older rows may be unknown because platform tracking was added later."
          )}
        </p>
        <SummaryGrid cards={[{
          label: aa("operating_system", "Operating system"),
          value: formatCoveragePercent(platformCompleteness.osFamilyCoveragePercent),
          detail: formatCoverageDetail(
            platformCompleteness.sessionsWithOsFamily,
            platformCompleteness.totalSessions
          )
        }, {
          label: aa("device_class", "Device class"),
          value: formatCoveragePercent(platformCompleteness.deviceClassCoveragePercent),
          detail: formatCoverageDetail(
            platformCompleteness.sessionsWithDeviceClass,
            platformCompleteness.totalSessions
          )
        }, {
          label: aa("browser", "Browser"),
          value: formatCoveragePercent(platformCompleteness.browserFamilyCoveragePercent),
          detail: formatCoverageDetail(
            platformCompleteness.sessionsWithBrowserFamily,
            platformCompleteness.totalSessions
          )
        }, {
          label: aa("app_mode", "App mode"),
          value: formatCoveragePercent(platformCompleteness.appPlatformCoveragePercent),
          detail: formatCoverageDetail(
            platformCompleteness.sessionsWithAppPlatform,
            platformCompleteness.totalSessions
          )
        }]} />

        <div className="admin-analytics-platforms__notes">
          <p className="admin-analytics-helper-note">
            {aa(
              "standalone_pwa_disclaimer",
              "Standalone/PWA mode is reported by the browser. It does not prove that the session came from Google Play, the App Store, or another store installation."
            )}
          </p>
          <p className="admin-analytics-helper-note">
            {aa(
              "play_store_downloads_disclaimer",
              "Play Store download totals are not recorded in ReelitPro session analytics and must be obtained from Google Play Console."
            )}
          </p>
        </div>
      </section>


      <SectionGrid>
        <CssBarChart title={aa("create_post_funnel", "Create Post funnel")} value={formatRate(funnel.postSubmitRate)} rows={funnelRows} getLabel={row => row.label} getValue={row => row.value} className="admin-analytics-funnel" />

        <CssBarChart title={aa("events_by_day", "Events by day")} rows={data?.eventsByDay} getLabel={row => formatDay(row?.day)} getValue={row => row?.events} getSublabel={row => formatUsersSublabel(row?.uniqueUsers)} />

        <CssBarChart title={aa("sessions_by_day", "Sessions by day")} rows={data?.sessionsByDay} getLabel={row => formatDay(row?.day)} getValue={row => row?.sessions} getSublabel={row => formatUsersSublabel(row?.uniqueUsers)} />

        <CssBarChart title={aa("average_session_duration_by_day", "Average session duration by day")} rows={data?.averageSessionDurationByDay} getLabel={row => formatDay(row?.day)} getValue={row => row?.averageSeconds} formatter={formatDuration} />

        <CssBarChart title={aa("top_feature_areas", "Top feature areas")} rows={data?.topFeatureAreas} getLabel={row => row?.featureArea || aa("unknown", "Unknown")} getValue={row => row?.eventCount} getSublabel={row => formatUsersSublabel(row?.uniqueUsers)} />

        <CssBarChart title={aa("top_event_names", "Top event names")} rows={data?.topEventNames} getLabel={row => row?.eventName || aa("unknown", "Unknown")} getValue={row => row?.eventCount} getSublabel={row => formatFeatureSublabel(row?.featureArea)} />

        <AdminAnalyticsTable title={aa("user_behavior", "User behavior")} rows={data?.userBehavior} rowKey={row => getUserSubLabel(row)} className="admin-analytics-table-panel--wide" minWidth={900} columns={[{
        header: aa("user", "User"),
        render: row => <UserCell title={getUserLabel(row)} subtitle={getUserSubLabel(row)} />
      }, {
        header: aa("events", "Events"),
        render: row => formatNumber(row?.eventCount)
      }, {
        header: aa("sessions", "Sessions"),
        render: row => formatNumber(row?.sessionCount)
      }, {
        header: aa("last_seen", "Last seen"),
        render: row => formatDateTime(row?.lastSeenAt)
      }, {
        header: aa("flags", "Flags"),
        render: row => <FlagList flags={behaviorFlags(row)} />
      }]} />

      </SectionGrid>
    </>;
}
